import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import moment from 'moment-timezone';
import 'moment/locale/el';

const resources = {
  en: {
    translation: {
      common: {
        appName: 'NYMERA',
        loadingWorkspace: 'Restoring your NYMERA workspace...',
        errorFallback: 'Something went wrong.',
        username: 'Username',
        email: 'Email',
        password: 'Password',
        language: 'Language',
        english: 'English',
        greek: 'Greek',
        lightTheme: 'Light theme',
        darkTheme: 'Dark theme',
        switchToLight: 'Switch to light theme',
        switchToDark: 'Switch to dark theme',
        noRecentDecision: 'No recent decision',
      },
      nav: {
        tagline: 'Decision intelligence for crypto markets',
        overview: 'Overview',
        decisions: 'Decisions',
        login: 'Log In',
        createAccount: 'Create Account',
        logout: 'Log Out',
      },
      auth: {
        highlights: {
          marketContext: 'High-signal market context',
          assetIntelligence: 'Decision-first asset intelligence',
          unifiedWorkspace: 'Sentiment and structure in one workspace',
        },
        login: {
          eyebrow: 'Member Access',
          title: 'Return to your decision workspace.',
          subtitle:
            'Sign in to review current opportunities, confidence-rated decisions, and the latest market context.',
          heading: 'Log In',
          body: 'Use your NYMERA username and password to continue.',
          usernamePlaceholder: 'Enter your username',
          passwordPlaceholder: 'Enter your password',
          submit: 'Log In',
          noAccount: 'New to NYMERA?',
          createAccountLink: 'Create an account',
          loginFailed: 'Login failed. Please try again.',
          tokenMissing: 'Token not received from server.',
          successAfterRegister: 'Account created successfully. Sign in to continue.',
        },
        register: {
          eyebrow: 'Start Your Account',
          title: 'Create your NYMERA workspace.',
          subtitle:
            'Set up your account for a cleaner view of market structure, sentiment, and decision support.',
          heading: 'Create Account',
          body: 'Create your account and continue into the platform.',
          usernamePlaceholder: 'Choose a username',
          emailPlaceholder: 'you@example.com',
          passwordPlaceholder: 'Create a secure password',
          confirmPassword: 'Confirm password',
          confirmPasswordPlaceholder: 'Repeat your password',
          passwordHelp: 'Minimum 6 characters.',
          submit: 'Create Account',
          hasAccount: 'Already have an account?',
          loginLink: 'Log in',
          registrationFailed: 'Registration failed.',
          passwordMismatch: 'Passwords do not match.',
        },
      },
      footer: {
        summary:
          'A calmer workspace for decision support, sentiment context, and structured market intelligence.',
        disclaimer: 'Use NYMERA as decision support, not as guaranteed financial advice.',
      },
      dashboard: {
        loadError: 'Unable to load the NYMERA market overview right now.',
        unavailable: 'Dashboard overview is unavailable.',
        heroEyebrow: 'Decision-first market view',
        heroTitle: 'NYMERA surfaces what deserves attention before the market gets noisy.',
        heroSubtitle:
          'Track highest-conviction decisions, sentiment context, and structural price levels in one calm operating view.',
        openDecisionCenter: 'Open Decision Center',
        reviewAsset: 'Review {{symbol}}',
        marketBalance: 'Market balance',
        trackedAssetsLabel: 'Assets under active intelligence coverage',
        bullishLabel: 'Bullish decisions in the current set',
        bearishLabel: 'Bearish decisions in the current set',
        averageSentimentLabel: 'Average sentiment across tracked assets',
        convictionLine: '{{label}} with {{score}}/100 derived conviction.',
        sentimentChip: '{{label}} sentiment',
        change24h: '{{value}}% 24h',
        fullIntelligence: 'View full intelligence',
        opportunitiesToReview: 'Opportunities to review',
        inspectAsset: 'Inspect asset',
        sentimentPulse: 'Sentiment pulse',
        latestDecisionFlow: 'Latest decision flow',
      },
      decisions: {
        loadError: 'Unable to load the decision center right now.',
        unavailable: 'Decision center unavailable.',
        title: 'Decision Center',
        subtitle:
          'Review the strongest current views first, then drill into the context behind each call.',
        highConviction: 'High-conviction opportunities',
        convictionSummary: '{{label}} · {{score}}/100 · {{sentiment}} sentiment',
        openAssetIntelligence: 'Open asset intelligence',
        decisionMix: 'Decision mix',
        bullishCount: '{{count}} bullish',
        bearishCount: '{{count}} bearish',
        neutralCount: '{{count}} neutral',
        watchlistSetups: 'Watchlist setups',
        review: 'Review',
        recentUpdates: 'Recent decision updates',
      },
      asset: {
        loadError: 'Unable to load asset intelligence right now.',
        unavailable: 'Asset intelligence is unavailable.',
        eyebrow: 'Asset Intelligence',
        subtitle: 'Decision context, sentiment, and structural market levels for the current setup.',
        confidenceChip: '{{score}}/100 {{label}}',
        sentimentChip: '{{label}} sentiment',
        backToOverview: 'Back to Overview',
        lastDecision: 'Last decision: {{value}}',
        price: 'Price',
        priceChange24h: '{{value}}% over 24h',
        decisionStreak: 'Decision streak',
        decisionStreakHelp: 'Consecutive recent cycles holding the same view.',
        supportDistance: 'Support distance',
        supportDistanceHelp: 'Distance to nearest support zone.',
        resistanceDistance: 'Resistance distance',
        resistanceDistanceHelp: 'Distance to nearest resistance zone.',
        priceStructure: 'Price structure',
        whyThisView: 'Why this view',
        keyLevels: 'Key levels',
        supportLevel: 'Support {{level}}',
        resistanceLevel: 'Resistance {{level}}',
        recentTrail: 'Recent decision trail',
      },
      chart: {
        shortView: '48-point view',
        fullscreen: 'Fullscreen',
        exitFullscreen: 'Exit fullscreen',
        price: 'Price',
        support: 'Support {{index}}',
        resistance: 'Resistance {{index}}',
        order: 'Order {{price}}',
      },
      realtime: {
        connected: 'Connected to real-time data server.',
        signal: 'Trade Signal: {{signal}} for {{symbol}}',
        connectionError: 'Socket connection error: {{message}}',
        disconnected: 'Disconnected from real-time data server.',
      },
    },
  },
  gr: {
    translation: {
      common: {
        appName: 'NYMERA',
        loadingWorkspace: 'Γίνεται επαναφορά του workspace NYMERA...',
        errorFallback: 'Κάτι πήγε στραβά.',
        username: 'Όνομα χρήστη',
        email: 'Email',
        password: 'Κωδικός',
        language: 'Γλώσσα',
        english: 'Αγγλικά',
        greek: 'Ελληνικά',
        lightTheme: 'Ανοιχτό θέμα',
        darkTheme: 'Σκούρο θέμα',
        switchToLight: 'Μετάβαση σε ανοιχτό θέμα',
        switchToDark: 'Μετάβαση σε σκούρο θέμα',
        noRecentDecision: 'Δεν υπάρχει πρόσφατη απόφαση',
      },
      nav: {
        tagline: 'Νοημοσύνη αποφάσεων για αγορές crypto',
        overview: 'Επισκόπηση',
        decisions: 'Αποφάσεις',
        login: 'Σύνδεση',
        createAccount: 'Δημιουργία λογαριασμού',
        logout: 'Αποσύνδεση',
      },
      auth: {
        highlights: {
          marketContext: 'Συμπυκνωμένο market context υψηλής αξίας',
          assetIntelligence: 'Asset intelligence με επίκεντρο την απόφαση',
          unifiedWorkspace: 'Συναίσθημα και δομή αγοράς σε ένα workspace',
        },
        login: {
          eyebrow: 'Πρόσβαση Μελών',
          title: 'Επιστρέψτε στο workspace αποφάσεών σας.',
          subtitle:
            'Συνδεθείτε για να δείτε τις τρέχουσες ευκαιρίες, τις αποφάσεις με confidence score και το πιο πρόσφατο market context.',
          heading: 'Σύνδεση',
          body: 'Χρησιμοποιήστε το όνομα χρήστη και τον κωδικό σας για να συνεχίσετε στο NYMERA.',
          usernamePlaceholder: 'Εισάγετε το όνομα χρήστη σας',
          passwordPlaceholder: 'Εισάγετε τον κωδικό σας',
          submit: 'Σύνδεση',
          noAccount: 'Νέος στο NYMERA;',
          createAccountLink: 'Δημιουργήστε λογαριασμό',
          loginFailed: 'Η σύνδεση απέτυχε. Προσπαθήστε ξανά.',
          tokenMissing: 'Δεν ελήφθη token από τον server.',
          successAfterRegister: 'Ο λογαριασμός δημιουργήθηκε. Συνδεθείτε για να συνεχίσετε.',
        },
        register: {
          eyebrow: 'Ξεκινήστε τον Λογαριασμό σας',
          title: 'Δημιουργήστε το workspace σας στο NYMERA.',
          subtitle:
            'Ρυθμίστε τον λογαριασμό σας για καθαρότερη εικόνα της δομής αγοράς, του sentiment και της υποστήριξης αποφάσεων.',
          heading: 'Δημιουργία λογαριασμού',
          body: 'Δημιουργήστε τον λογαριασμό σας και συνεχίστε στην πλατφόρμα.',
          usernamePlaceholder: 'Επιλέξτε όνομα χρήστη',
          emailPlaceholder: 'you@example.com',
          passwordPlaceholder: 'Δημιουργήστε έναν ασφαλή κωδικό',
          confirmPassword: 'Επιβεβαίωση κωδικού',
          confirmPasswordPlaceholder: 'Επαναλάβετε τον κωδικό σας',
          passwordHelp: 'Τουλάχιστον 6 χαρακτήρες.',
          submit: 'Δημιουργία λογαριασμού',
          hasAccount: 'Έχετε ήδη λογαριασμό;',
          loginLink: 'Συνδεθείτε',
          registrationFailed: 'Η εγγραφή απέτυχε.',
          passwordMismatch: 'Οι κωδικοί δεν ταιριάζουν.',
        },
      },
      footer: {
        summary:
          'Ένα πιο ήρεμο workspace για υποστήριξη αποφάσεων, context συναισθήματος και δομημένη market intelligence.',
        disclaimer: 'Χρησιμοποιήστε το NYMERA ως υποστήριξη αποφάσεων και όχι ως εγγυημένη επενδυτική συμβουλή.',
      },
      dashboard: {
        loadError: 'Δεν ήταν δυνατή η φόρτωση της επισκόπησης αγοράς του NYMERA αυτή τη στιγμή.',
        unavailable: 'Η επισκόπηση dashboard δεν είναι διαθέσιμη.',
        heroEyebrow: 'Θέα αγοράς με επίκεντρο την απόφαση',
        heroTitle: 'Το NYMERA αναδεικνύει ό,τι αξίζει προσοχή πριν η αγορά γεμίσει θόρυβο.',
        heroSubtitle:
          'Παρακολουθήστε αποφάσεις υψηλής βεβαιότητας, sentiment context και βασικά επίπεδα τιμών σε μία ήρεμη επιχειρησιακή εικόνα.',
        openDecisionCenter: 'Άνοιγμα Κέντρου Αποφάσεων',
        reviewAsset: 'Εξέταση {{symbol}}',
        marketBalance: 'Ισορροπία αγοράς',
        trackedAssetsLabel: 'Assets που καλύπτονται ενεργά από το intelligence layer',
        bullishLabel: 'Ανοδικές αποφάσεις στο τρέχον σύνολο',
        bearishLabel: 'Πτωτικές αποφάσεις στο τρέχον σύνολο',
        averageSentimentLabel: 'Μέσο sentiment στα παρακολουθούμενα assets',
        convictionLine: '{{label}} με παράγωγη βεβαιότητα {{score}}/100.',
        sentimentChip: '{{label}} sentiment',
        change24h: '{{value}}% 24ωρο',
        fullIntelligence: 'Πλήρες intelligence',
        opportunitiesToReview: 'Ευκαιρίες για ανασκόπηση',
        inspectAsset: 'Έλεγχος asset',
        sentimentPulse: 'Παλμός sentiment',
        latestDecisionFlow: 'Τελευταία ροή αποφάσεων',
      },
      decisions: {
        loadError: 'Δεν ήταν δυνατή η φόρτωση του κέντρου αποφάσεων αυτή τη στιγμή.',
        unavailable: 'Το κέντρο αποφάσεων δεν είναι διαθέσιμο.',
        title: 'Κέντρο Αποφάσεων',
        subtitle:
          'Δείτε πρώτα τις ισχυρότερες τρέχουσες απόψεις και έπειτα εξετάστε το context πίσω από κάθε απόφαση.',
        highConviction: 'Ευκαιρίες υψηλής βεβαιότητας',
        convictionSummary: '{{label}} · {{score}}/100 · sentiment {{sentiment}}',
        openAssetIntelligence: 'Άνοιγμα asset intelligence',
        decisionMix: 'Μείγμα αποφάσεων',
        bullishCount: '{{count}} ανοδικές',
        bearishCount: '{{count}} πτωτικές',
        neutralCount: '{{count}} ουδέτερες',
        watchlistSetups: 'Setups watchlist',
        review: 'Εξέταση',
        recentUpdates: 'Πρόσφατες ενημερώσεις αποφάσεων',
      },
      asset: {
        loadError: 'Δεν ήταν δυνατή η φόρτωση του asset intelligence αυτή τη στιγμή.',
        unavailable: 'Το asset intelligence δεν είναι διαθέσιμο.',
        eyebrow: 'Asset Intelligence',
        subtitle: 'Context απόφασης, sentiment και βασικά επίπεδα αγοράς για το τρέχον setup.',
        confidenceChip: '{{score}}/100 {{label}}',
        sentimentChip: '{{label}} sentiment',
        backToOverview: 'Επιστροφή στην Επισκόπηση',
        lastDecision: 'Τελευταία απόφαση: {{value}}',
        price: 'Τιμή',
        priceChange24h: '{{value}}% στο 24ωρο',
        decisionStreak: 'Σερί αποφάσεων',
        decisionStreakHelp: 'Συνεχόμενοι πρόσφατοι κύκλοι με την ίδια άποψη.',
        supportDistance: 'Απόσταση από στήριξη',
        supportDistanceHelp: 'Απόσταση από την πλησιέστερη ζώνη στήριξης.',
        resistanceDistance: 'Απόσταση από αντίσταση',
        resistanceDistanceHelp: 'Απόσταση από την πλησιέστερη ζώνη αντίστασης.',
        priceStructure: 'Δομή τιμής',
        whyThisView: 'Γιατί αυτή η άποψη',
        keyLevels: 'Κρίσιμα επίπεδα',
        supportLevel: 'Στήριξη {{level}}',
        resistanceLevel: 'Αντίσταση {{level}}',
        recentTrail: 'Πρόσφατο ιστορικό αποφάσεων',
      },
      chart: {
        shortView: 'Προβολή 48 σημείων',
        fullscreen: 'Πλήρης οθόνη',
        exitFullscreen: 'Έξοδος από πλήρη οθόνη',
        price: 'Τιμή',
        support: 'Στήριξη {{index}}',
        resistance: 'Αντίσταση {{index}}',
        order: 'Εντολή {{price}}',
      },
      realtime: {
        connected: 'Συνδέθηκε στον real-time server.',
        signal: 'Σήμα συναλλαγής: {{signal}} για {{symbol}}',
        connectionError: 'Σφάλμα σύνδεσης socket: {{message}}',
        disconnected: 'Η σύνδεση με τον real-time server διακόπηκε.',
      },
    },
  },
};

function applyLocale(lang) {
  const normalized = lang === 'gr' ? 'el' : 'en';
  moment.locale(normalized);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = normalized;
  }
}

i18n.use(initReactI18next).init({
  resources,
  lng: 'en',
  fallbackLng: 'en',
  supportedLngs: ['en', 'gr'],
  interpolation: {
    escapeValue: false,
  },
  returnNull: false,
});

applyLocale(i18n.language);
i18n.on('languageChanged', applyLocale);

export default i18n;
